import React from 'react';
import * as yup from 'yup';
import { Avatar, Box, Divider } from '@mui/material';
import { SparkForm, SparkDropdown } from 'sharedComponents/SparkForm';
import { SparkAccordion } from 'sharedComponents/UI';
import TypeDetailCard from 'components/CreateSpark/Modals/SelectStepTypeModal/TypeDetailCard';
import { SuccessStatusIcon, WarningStatusIcon } from 'components/SVG';
import { useUpdateSparkStep } from 'hooks/SparkBuilder/sparkStep';
import { useDialog } from 'hooks/common';
import { getInitialStringValue } from 'utilities/form';
import { parseJSONCleaned } from 'utilities/index';
import { useSparkBuilderStore } from 'stores';
import {
  useStepParametersByStepID,
  useUpdateStepParameter,
} from 'hooks/SparkBuilder/parameters/stepParameter';
import { usePluginFunctionParameters } from 'hooks/SparkBuilder/parameters';
import { useUiComponents } from 'hooks/SparkFormBuilder';
import { PluginConnectionAuthentication } from './PluginConnectionAuthentication';
import { StepTest } from 'types/SparkStep';
import PluginConfigForm from './PluginConfigForm';
import PluginFormButton from './PluginFormButton';
import type { PluginFunctions, Plugins } from 'types/Plugins';
import type { PluginsFormType } from './PluginsFormContainer';
import type { DropdownOption } from 'types/common/DropdownOption';
import type { PluginConfigFormValues } from './types';
import PluginTestAccordion from './PluginTestAccordion';
import { usePluginConnectionByID } from 'hooks/SparkBuilder/pluginFunction';
import { useIsConnectionValid } from './PluginConnectionAuthentication/utils';

type PluginsProps = {
  pluginFunctionID: string | null;
  pluginFunctionDropdownOptions: DropdownOption[];
  selectedPlugins: Plugins | undefined;
  handleTestClick: () => void;
  pluginFunctions: PluginFunctions[] | undefined;
};

export default function PluginsForm({
  pluginFunctionID,
  pluginFunctionDropdownOptions,
  handleTestClick,
  selectedPlugins,
  pluginFunctions,
}: PluginsProps) {
  const [pluginAuthenticationID, setPluginAuthenticationID] =
    React.useState('');

  const [showTestAccordion, setShowTestAccordion] = React.useState(false);
  const [showConfigAccordion, setShowConfigAccordion] = React.useState(false);

  const { sparkID, sparkStep } = useSparkBuilderStore((store) => ({
    sparkID: store.sparkID,
    sparkStep: store.sparkStepsActions.getSparkStep(
      store.builderSideBar.selectedStep?.id || ''
    ),
  }));

  const [selectedPluginConnectionID, setSelectedPluginConnectionID] =
    React.useState<string | null>(
      sparkStep?.attributes?.pluginConnectionID || ''
    );
  const checkConnectionValidity = useIsConnectionValid();

  const { isLoading: isLoadingStepParameters } = useStepParametersByStepID(
    sparkStep?.id
  );

  const { isLoading: isUiComponentLoading } = useUiComponents();

  const {
    data: { data: pluginFunctionParameters } = {},
    isLoading: isLoadingPluginFunctionParameter,
  } = usePluginFunctionParameters(pluginFunctionID);

  const shouldBypassConfig = React.useMemo(
    () =>
      pluginFunctionParameters?.length === 0 &&
      !isLoadingPluginFunctionParameter,
    [isLoadingPluginFunctionParameter, pluginFunctionParameters?.length]
  );

  const sparkTestResults = parseJSONCleaned<StepTest>(
    sparkStep?.attributes.testResults || ''
  );
  const isOutputAvailable = !!sparkTestResults?.SparkTask.Output;

  const { mutateAsync: updateSparkStep, isLoading: isUpdatingSparkStep } =
    useUpdateSparkStep(sparkID);

  const {
    mutateAsync: updateStepParameter,
    isLoading: isUpdatingStepParameters,
  } = useUpdateStepParameter(sparkStep?.id);

  const handleConnectionSelect = (connectionID: string) => {
    setSelectedPluginConnectionID(connectionID);
  };
  const initialValues = React.useMemo(() => {
    return {
      stepTitle: sparkStep?.attributes.name,
      pluginsID: selectedPlugins?.id ?? '',
      parameters: [],
      pluginFunctionID: getInitialStringValue(pluginFunctionID) ?? null,
      pluginConnectionID: sparkStep?.attributes.pluginConnectionID || '',
    };
  }, [
    sparkStep?.attributes.name,
    sparkStep?.attributes.pluginConnectionID,
    selectedPlugins?.id,
    pluginFunctionID,
  ]);

  const handleFunctionChange = React.useCallback(
    async (functionID: string | number) => {
      const pluginFunction = pluginFunctions?.find(
        (pluginFunction) => pluginFunction.id === `${functionID}`
      );

      const authID = pluginFunction?.attributes.pluginAuthenticationID;

      setPluginAuthenticationID(authID || '');
    },
    [pluginFunctions]
  );

  const onChangeFunction = React.useCallback(
    async (functionID: string | number) => {
      await updateSparkStep({
        stepID: sparkStep?.id,
        pluginFunctionID: functionID as string,
      });
    },
    [sparkStep?.id, updateSparkStep]
  );

  const { pluginConnection, isLoading: isSinglePluginConnectionLoading } =
    usePluginConnectionByID(selectedPluginConnectionID);

  const {
    pluginConnection: stepPluginConnection,
    isLoading: isStepPluginConnectionLoading,
  } = usePluginConnectionByID(sparkStep?.attributes?.pluginConnectionID);

  React.useEffect(() => {
    if (!pluginAuthenticationID && pluginFunctionID) {
      handleFunctionChange(pluginFunctionID);
    }
  }, [handleFunctionChange, pluginFunctionID, pluginAuthenticationID]);

  const stepMetaDiagnostic = sparkStep?.meta.diagnostics;

  const pluginDialogConfig = useDialog(!isOutputAvailable);
  const configDialogConfig = useDialog(false);
  const testDialogConfig = useDialog(isOutputAvailable);

  const handlePluginOpen = () => {
    pluginDialogConfig.openDialog();
    testDialogConfig.closeDialog();
    configDialogConfig.closeDialog();
  };
  const handleConfigOpen = () => {
    pluginDialogConfig.closeDialog();
    testDialogConfig.closeDialog();
    configDialogConfig.openDialog();
  };
  const handleTestOpen = React.useCallback(() => {
    pluginDialogConfig.closeDialog();
    testDialogConfig.openDialog();
    configDialogConfig.closeDialog();
  }, [configDialogConfig, pluginDialogConfig, testDialogConfig]);

  const isSelectedConnectionValid = checkConnectionValidity(
    pluginConnection?.attributes.pluginConnectionStatusTypeID
  );
  const isStepConnectionValid = checkConnectionValidity(
    stepPluginConnection?.attributes.pluginConnectionStatusTypeID
  );

  const additionalCheckForConnection = pluginAuthenticationID
    ? !!(selectedPluginConnectionID && isSelectedConnectionValid)
    : true;

  const additionalCheckForExistingConnection = pluginAuthenticationID
    ? isStepConnectionValid
    : true;

  React.useEffect(() => {
    const hasIssues =
      !stepMetaDiagnostic?.hasPluginConfigIssues &&
      !stepMetaDiagnostic?.hasPluginIssues &&
      additionalCheckForConnection;

    setShowTestAccordion(hasIssues);
  }, [
    additionalCheckForConnection,
    pluginFunctionID,
    stepMetaDiagnostic?.hasPluginConfigIssues,
    stepMetaDiagnostic?.hasPluginIssues,
  ]);

  const handleConfigSubmit = async (formValues: PluginConfigFormValues) => {
    const promises = Object.entries(formValues).map(([key, value]) =>
      updateStepParameter({ stepParameterID: key, value: value })
    );
    await Promise.all(promises);

    handleTestOpen();
  };
  const handleSubmit = async (formValues: PluginsFormType) => {
    const { stepTitle, pluginFunctionID, pluginConnectionID } = formValues;

    await updateSparkStep({
      stepID: sparkStep?.id,
      name: stepTitle,
      pluginFunctionID: pluginFunctionID as string,
      ...(pluginConnectionID && { pluginConnectionID }),
    });
    setShowConfigAccordion(!!pluginFunctionID && additionalCheckForConnection);

    if (shouldBypassConfig) {
      handleTestOpen();
    } else {
      handleConfigOpen();
    }
  };

  const isLoadingConfigData =
    isLoadingPluginFunctionParameter ||
    isLoadingStepParameters ||
    isUiComponentLoading ||
    isSinglePluginConnectionLoading ||
    isStepPluginConnectionLoading;

  const isMutating = isUpdatingSparkStep || isUpdatingStepParameters;

  const testAccordionChecks =
    showTestAccordion &&
    additionalCheckForConnection &&
    additionalCheckForExistingConnection;

  const configAccordionChecks =
    showConfigAccordion &&
    !shouldBypassConfig &&
    additionalCheckForConnection &&
    additionalCheckForExistingConnection;

  return (
    <>
      <Box width='100%'>
        <SparkAccordion
          title='Plugin'
          sx={{ border: 'none' }}
          dialogConfig={{ ...pluginDialogConfig, openDialog: handlePluginOpen }}
          accordionTitleProps={{ variant: 'h5' }}
          summaryStyles={{ paddingX: 2, paddingY: 1, cursor: 'pointer' }}
          detailsStyle={{ paddingBottom: 2 }}
          icon={
            <Avatar
              sx={{
                width: '30px',
                height: '30px',
              }}
            >
              {stepMetaDiagnostic?.hasPluginIssues ? (
                <WarningStatusIcon width='100%' height='100%' />
              ) : (
                <SuccessStatusIcon width='100%' height='100%' />
              )}
            </Avatar>
          }
        >
          <SparkForm<PluginsFormType>
            validationSchema={yup.object({
              pluginFunctionID: yup.string().required('Function is required'),
            })}
            initialValues={initialValues}
            onSave={handleSubmit}
            sx={{ paddingX: 2, paddingY: 1.5 }}
          >
            <Box width='100%' display='flex' flexDirection='column'>
              <TypeDetailCard
                icon={selectedPlugins?.attributes.logoURL || ''}
                name={selectedPlugins?.attributes.name || ''}
                description={selectedPlugins?.attributes.description || ''}
                sx={{
                  cursor: 'default',
                  marginBottom: 2,
                }}
              />
              <SparkDropdown
                name='pluginFunctionID'
                label='Function'
                placeholder='Select Function'
                size={12}
                onChange={(functionID) => {
                  handleFunctionChange(functionID);
                  onChangeFunction(functionID);
                }}
              >
                {pluginFunctionDropdownOptions || []}
              </SparkDropdown>
            </Box>
            {pluginAuthenticationID && (
              <PluginConnectionAuthentication
                selectedPlugins={selectedPlugins}
                handleConnectionSelect={handleConnectionSelect}
                pluginAuthenticationID={pluginAuthenticationID}
              />
            )}
            <PluginFormButton
              pluginAuthenticationID={pluginAuthenticationID}
              isMutating={isMutating}
            />
          </SparkForm>
        </SparkAccordion>
        <Divider />
        {configAccordionChecks && (
          <PluginConfigForm
            dialogConfig={{
              ...configDialogConfig,
              openDialog: handleConfigOpen,
            }}
            handleConfigSubmit={handleConfigSubmit}
            isLoadingConfigData={isLoadingConfigData}
            pluginFunctionID={pluginFunctionID}
            isMutating={isMutating}
          />
        )}
        {testAccordionChecks && (
          <PluginTestAccordion
            dialogConfig={{ ...testDialogConfig, openDialog: handleTestOpen }}
            hasTestIssues={!!stepMetaDiagnostic?.hasTestIssues}
            handleTestClick={handleTestClick}
            sparkTestResults={sparkTestResults}
          />
        )}
      </Box>
    </>
  );
}
